import { QRCodeSVG } from "qrcode.react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import { getPosClient } from "../../lib/api";
import { useFocusTrap } from "../../lib/use-focus-trap";
import { formatTime } from "../../lib/format";
import { Alert } from "../ui/Alert";
import { Button } from "../ui/Button";
import { InlineSpinner } from "../ui/Spinner";
import { useActiveTimezone } from "../../lib/useActiveTimezone";

interface KioskHandoffModalProps {
  readonly orderId: string;
  readonly onClose: () => void;
}

export function KioskHandoffModal({ orderId, onClose }: KioskHandoffModalProps) {
  const { t } = useTranslation();
  const tz = useActiveTimezone();
  const dialogRef = useFocusTrap<HTMLDivElement>(true, onClose);

  const [qrPayload, setQrPayload] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setQrPayload(null);
    setExpiresAt(null);
    getPosClient()
      .createKioskHandoff(orderId)
      .then((result) => {
        if (!cancelled) {
          setQrPayload(result.qr_payload);
          setExpiresAt(result.expires_at);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(t("kioskHandoff.error"));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, t]);

  const expiresFormatted = expiresAt ? formatTime(expiresAt, tz) : null;

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kiosk-handoff-dialog-title"
    >
      <div className="relative w-full max-w-sm rounded-xl bg-surface p-6 shadow-elev-3">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 rounded-md p-1 text-muted hover:bg-surface-muted hover:text-strong"
        >
          <X className="h-4 w-4" />
        </button>

        <h2
          id="kiosk-handoff-dialog-title"
          className="text-lg font-bold tracking-tight text-strong"
        >
          {t("kioskHandoff.modalTitle")}
        </h2>
        <p className="mt-1 text-sm text-muted">{t("kioskHandoff.body")}</p>

        <div className="mt-5 flex flex-col items-center gap-4">
          {loading && (
            <div className="flex h-36 w-36 items-center justify-center">
              <InlineSpinner />
              <span className="sr-only">{t("kioskHandoff.loading")}</span>
            </div>
          )}
          {!loading && error && <Alert tone="error">{error}</Alert>}
          {!loading && qrPayload && (
            <>
              <div className="rounded-lg border border-line bg-white p-3">
                <QRCodeSVG
                  value={qrPayload}
                  size={180}
                  marginSize={1}
                  aria-label={t("kioskHandoff.qrAlt")}
                />
              </div>
              {expiresFormatted && (
                <p className="text-xs text-muted">
                  {t("kioskHandoff.expiresLabel")}:{" "}
                  <span className="font-semibold tabular-nums text-foreground">
                    {expiresFormatted}
                  </span>
                </p>
              )}
            </>
          )}
        </div>

        <div className="mt-5 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t("kioskHandoff.cancelHandoff")}
          </Button>
        </div>
      </div>
    </div>
  );
}
